import { useEffect, useState } from 'react';
import { api, ApiRequestError, type PersonStat, type StatsResponse } from '../api.ts';
import { useSession } from '../session.tsx';
import { Avatar, Banner, Button, Card, Spinner } from '../components/ui.tsx';
import { GameHistory } from '../components/GameHistory.tsx';

/**
 * The stats page.
 *
 * Everything behind the member code — there is no public page, by design. The
 * numbers are recomputed from the tables on every request rather than kept as
 * counters, so a stat invented later applies to every game already played.
 *
 * The ordering here is a judgement about what people care about. Win rate and
 * games won are the scoreboard, but Nemesis and Twin are the ones that get read
 * out loud, so they are not buried at the bottom of a table.
 */

function percent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}

function hours(ms: number): string {
  if (ms <= 0) return '—';
  const h = ms / 3_600_000;
  return h < 1 ? `${Math.round(ms / 60_000)}m` : `${h.toFixed(1)}h`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-xs text-text-faint">{label}</p>
      <p className="truncate text-lg font-semibold tabular-nums">{value}</p>
      {/* Not truncated: "votes for the eventual champion" is the only thing
          that explains what Kingmaker counts, and cutting it to "the eventual
          c…" leaves a number nobody can read. */}
      {hint && <p className="text-xs leading-tight text-text-faint">{hint}</p>}
    </div>
  );
}

function PersonCard({ person, isYou }: { person: PersonStat; isYou: boolean }) {
  const [open, setOpen] = useState(isYou);

  return (
    <Card className={isYou ? 'border-accent/40' : undefined}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 text-left"
      >
        <Avatar value={person.avatarValue} size="md" />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2">
            <span className="truncate font-medium">{person.displayName}</span>
            {isYou && (
              <span className="rounded bg-accent/15 px-1.5 py-0.5 text-xs text-accent">you</span>
            )}
            {/* The handle appears only when it differs, so it is a
                disambiguator rather than noise. */}
            {person.username !== person.displayName && (
              <span className="truncate text-xs text-text-faint">{person.username}</span>
            )}
          </p>
          <p className="text-xs text-text-faint">
            {person.gamesWon} {person.gamesWon === 1 ? 'win' : 'wins'} ·{' '}
            {percent(person.matchWinRate)} of matches · {person.gamesPlayed} played
          </p>
        </div>
        <span aria-hidden className="shrink-0 text-xs text-text-faint">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Games won" value={String(person.gamesWon)} />
            <Stat label="Match win rate" value={percent(person.matchWinRate)} hint={`${person.matchesWon} of ${person.matchesPlayed}`} />
            <Stat label="Votes received" value={String(person.votesReceived)} />
            <Stat
              label="The ratio"
              value={person.votesPerEntry === null ? '—' : person.votesPerEntry.toFixed(1)}
              hint="votes per entry"
            />
            <Stat label="Finals reached" value={String(person.finalsReached)} />
            <Stat label="Heartbreak" value={String(person.finalsLost)} hint="finals lost" />
            <Stat label="First-round exits" value={String(person.firstRoundExits)} />
            <Stat label="Longest streak" value={String(person.longestWinStreak)} />
            <Stat label="By one" value={`${person.wonByOne}–${person.lostByOne}`} hint="won–lost by a single vote" />
            <Stat label="Kingmaker" value={String(person.kingmaker)} hint="votes for the eventual champion" />
            <Stat label="Votes cast" value={String(person.votesCast)} />
            <Stat label="Listening" value={hours(person.listeningMs)} />
          </div>

          {(person.nemesis || person.twin) && (
            <div className="mt-4 flex flex-col gap-2 border-t border-border pt-3 text-sm">
              {person.nemesis && (
                <p>
                  <span className="text-text-faint">Nemesis · </span>
                  <strong>{person.nemesis.displayName}</strong> has knocked them out{' '}
                  {person.nemesis.count} {person.nemesis.count === 1 ? 'time' : 'times'}.
                </p>
              )}
              {person.twin && (
                <p>
                  <span className="text-text-faint">Twin · </span>
                  picks the same thing as <strong>{person.twin.displayName}</strong>{' '}
                  {person.twin.count} {person.twin.count === 1 ? 'time' : 'times'}.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function Stats({ onBack }: { onBack: () => void }) {
  const { session } = useSession();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .stats()
      .then(setStats)
      .catch((caught) =>
        setError(caught instanceof ApiRequestError ? caught.message : 'Could not load stats.'),
      );
  }, []);

  if (error) {
    return (
      <main className="mx-auto w-full max-w-lg px-4 py-12">
        <Banner tone="danger">{error}</Banner>
        <div className="mt-4">
          <Button onClick={onBack}>Back</Button>
        </div>
      </main>
    );
  }

  if (!stats) return <Spinner label="Working out the numbers" />;

  const { collective } = stats;

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold tracking-tight">Stats</h1>
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
      </header>

      {stats.emptyReason ? (
        <Banner>{stats.emptyReason}</Banner>
      ) : (
        <>
          <Card className="mb-6">
            <h2 className="mb-3 font-medium">All time</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
              <Stat label="Games" value={String(collective.gamesPlayed)} />
              <Stat label="Matches" value={String(collective.matchesPlayed)} />
              <Stat label="Entries" value={String(collective.entriesSubmitted)} />
              <Stat label="Votes" value={String(collective.votesCast)} />
              <Stat label="Listened" value={hours(collective.listeningMs)} />
            </div>

            {/* Said out loud rather than silently dropped: a solo game is a
                perfectly good way to spend an evening, it is just not evidence
                about who is any good at this. */}
            {collective.soloGamesExcluded > 0 && (
              <p className="mt-4 border-t border-border pt-3 text-sm text-text-faint">
                {collective.soloGamesExcluded} solo{' '}
                {collective.soloGamesExcluded === 1 ? 'game' : 'games'} not counted.
              </p>
            )}

            {collective.mostSubmitted && (
              <p className="mt-4 border-t border-border pt-3 text-sm">
                <span className="text-text-faint">Most submitted · </span>
                <strong>{collective.mostSubmitted.title}</strong>, {collective.mostSubmitted.times}{' '}
                times — {collective.mostSubmitted.wins}–{collective.mostSubmitted.losses} in matches.
              </p>
            )}
          </Card>

          <h2 className="mb-3 font-medium">
            The table <span className="text-sm text-text-faint">by games won</span>
          </h2>
          <ul className="flex flex-col gap-3">
            {stats.people.map((person) => (
              <li key={person.userId}>
                <PersonCard person={person} isYou={person.userId === session?.user.id} />
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Outside the empty branch on purpose: a night of solo games has no
          table to show but still has games worth reading back. */}
      <GameHistory games={stats.recentGames} title="Recent games" />
    </main>
  );
}
